import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  TextField,
  Chip,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
// import TEMPLATES from '../../Templates.json';
import { useMemo, useState } from 'react';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { ITemplate, Question } from '../../components/RunRiskAssessment';
import { Toast, Utils } from 'shared/helpers';
import { RiskAssessmentService } from 'services/RiskAssessmentService';

interface IQuestionProps {
  question: Question;
  index: number;
  onChange: (value: any) => void;
  defaultValue?: any;
}
const SelectOne: React.FC<IQuestionProps> = (props) => {
  const { question, index, defaultValue, onChange } = props;
  const { options, id, text } = question;
  const [value, setValue] = useState(defaultValue);
  return (
    <FormControl fullWidth className="mb-1 mt-4">
      <FormLabel className="text-black" id={id}>
        {index + 1}.{text}
      </FormLabel>
      <RadioGroup
        value={value}
        defaultValue={defaultValue}
        name={id}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
      >
        {Utils.isStringArray(options) &&
          options?.map((option: any) => (
            <FormControlLabel
              key={`${id}_${option}`}
              value={option}
              control={<Radio />}
              label={option}
            />
          ))}
        {!Utils.isStringArray(options) &&
          options?.map((option: any) => (
            <FormControlLabel
              key={`${id}_${option.label}`}
              value={option.label}
              control={<Radio />}
              label={option.label}
            />
          ))}
      </RadioGroup>
    </FormControl>
  );
};

const SelectMany: React.FC<IQuestionProps> = (props) => {
  const { question, index, defaultValue, onChange } = props;
  const { options, id, text } = question;

  const [value, setValue] = useState<string[]>(defaultValue || []);

  const handleChange = (optionValue: string): void => {
    const updatedValue = value.includes(optionValue)
      ? value.filter((item) => item !== optionValue)
      : [...value, optionValue];

    setValue(updatedValue);
    onChange(updatedValue);
  };

  const normalizedOptions =
    options?.map((option) =>
      typeof option === 'string' ? option : option.label
    ) || [];

  return (
    <FormControl fullWidth className="mb-1 mt-4">
      <FormLabel className="text-black" id={id}>
        {index + 1}.{text}
      </FormLabel>

      {normalizedOptions.map((option) => (
        <FormControlLabel
          key={`${id}_${option}`}
          control={
            <Checkbox
              checked={value.includes(option)}
              onChange={() => handleChange(option)}
            />
          }
          label={option}
        />
      ))}
    </FormControl>
  );
};

const ATextField: React.FC<IQuestionProps> = (props) => {
  const { question, index, defaultValue, onChange } = props;
  const { id, text, type } = question;
  const [value, setValue] = useState(defaultValue);
  return (
    <FormControl fullWidth className="mb-1 mt-4">
      <FormLabel className="text-black mb-2" id={id}>
        {index + 1}.{text}
      </FormLabel>
      <TextField
        className="mb-1"
        placeholder="Enter your answer"
        multiline={type === 'LongText'}
        rows={type === 'LongText' ? 3 : 1}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        onBlur={(e) => {
          onChange(e.target.value);
        }}
      />
    </FormControl>
  );
};

interface RiskAssessmentDetailProps {
  quickEdit?: boolean;
  assessmentIds: any[];
  templateDetail: ITemplate;
  datasets: any[];
  aiSystems: any[];
  userResponses: { [key: string]: any };
  onFinish?: (reload: boolean) => void;
  guestMode: boolean;
}
const RiskAssessmentDetail: React.FC<RiskAssessmentDetailProps> = (props) => {
  const {
    templateDetail,
    quickEdit,
    assessmentIds,
    datasets,
    aiSystems,
    userResponses,
    onFinish,
    guestMode,
  } = props;
  const { mode } = useParams();
  const [currentSection, setCurrentSection] = useState(0);
  const [subsection, setSubsection] = useState(0);
  const [userResponse, setUserResponse] = useState<{ [key: string]: any }>(
    userResponses
  );
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetails] = useState(false);

  console.log(templateDetail, 'templateDetailtemplateDetailtemplateDetail');

  // Instance methods
  const isEditMode = useMemo(() => mode === 'edit', [mode]);
  // const totalSections = useMemo(() => {
  //   if (templateDetail) {
  //     return templateDetail.sections.length;
  //   }
  //   return 0;
  // }, [templateDetail]);
  // const subsections = useMemo(() => {
  //   if (templateDetail) {
  //     return templateDetail.sections[currentSection].subsections;
  //   }
  //   return [];
  // }, [currentSection, templateDetail]);

  // const questions = useMemo(() => {
  //   if (templateDetail) {
  //     return templateDetail.sections[currentSection].subsections[subsection]
  //       .questions;
  //   }
  //   return [];
  // }, [currentSection, subsection, templateDetail]);

  const subsections = useMemo(() => {
    if (templateDetail?.sections?.[currentSection]?.subsections) {
      return templateDetail.sections[currentSection].subsections;
    }
    return [];
  }, [currentSection, templateDetail]);

  const questions = useMemo(() => {
    if (
      templateDetail?.sections?.[currentSection]?.subsections?.[subsection]
        ?.questions
    ) {
      return templateDetail.sections[currentSection].subsections[subsection]
        .questions;
    }
    return [];
  }, [currentSection, subsection, templateDetail]);

  const totalSections = useMemo(
    () => templateDetail?.sections?.length ?? 0,
    [templateDetail]
  );

  const isBulkAssessment = useMemo(
    () => datasets.length + aiSystems.length > 1,
    [datasets, aiSystems]
  );

  const preparePayload = (
    status: 'In Progress' | 'Completed'
  ): Promise<any[]> =>
    new Promise((resolve, reject) => {
      const payload: any[] = [];
      if (assessmentIds.length > 0) {
        assessmentIds.forEach((a: any) =>
          payload.push({
            /* eslint no-underscore-dangle: 0 */
            _id: a._id,
            responses: userResponse,
            status,
          })
        );
        resolve(payload);
      } else {
        // Create new assessment
        const createPayload = {
          templateId: templateDetail.id,
          datasetId: datasets.length > 0 ? datasets[0]._id : null,
          aiInventoryId: aiSystems.length > 0 ? aiSystems[0]._id : null,
          status,
          assessmentType: 'self',
        };
        RiskAssessmentService.submitRiskAssessment(createPayload)
          .then((response) => {
            const { data } = response.data;
            payload.push({
              /* eslint no-underscore-dangle: 0 */
              _id: data._id,
              responses: userResponse,
              status,
            });
            resolve(payload);
          })
          .catch((error) => {
            reject(error);
          });
      }
    });

  const scrollToTop = (): void => {
    setTimeout(() => {
      const element = document.getElementById('question-start');
      element?.scrollIntoView({ behavior: 'smooth' });
    }, 200);
  };

  // API Calls
  const submitAssessment = (payloads: any[], callback: any): void => {
    const promises: any[] = [];
    payloads.forEach((payload) =>
      promises.push(
        guestMode
          ? RiskAssessmentService.completeExternalAssessment({
              responses: userResponse,
            })
          : RiskAssessmentService.submitRiskAssessmentResponse(payload)
      )
    );
    Promise.all(promises)
      .then(() => {
        callback(true);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };

  // UI Actions
  const actionOnClickBack = (): void => {
    if (subsection === 0) {
      setCurrentSection((prev) => prev - 1);
      setSubsection(
        () => templateDetail.sections[currentSection - 1].subsections.length - 1
      );
    } else {
      setSubsection((prev) => prev - 1);
    }
    scrollToTop();
  };

  const actionOnClickNext = (): void => {
    if (subsection + 1 === subsections.length) {
      setCurrentSection((prev) => prev + 1);
      setSubsection(() => 0);
    } else {
      setSubsection((prev) => prev + 1);
    }
    scrollToTop();
  };

  const actionBackToAssessments = (reload: boolean): void => {
    if (quickEdit) {
      onFinish?.(reload);
    } else if (datasets && datasets.length > 0) {
      navigate(`/datasets/records/${datasets[0]._id}`, {
        state: { from: 'view-details' },
      });
    } else if (aiSystems && aiSystems.length > 0) {
      navigate(`/ai-systems/records/${aiSystems[0]._id}`, {
        state: { from: 'view-details' },
      });
    } else {
      navigate(-1);
    }
  };

  const validateAllResponses = (): boolean => {
    if (!templateDetail?.sections) {
      return false;
    }

    const unansweredQuestions: {
      question: Question;
      sectionIndex: number;
      subsectionIndex: number;
    }[] = [];

    templateDetail.sections.forEach((section: any, sectionIndex: number) => {
      section.subsections.forEach(
        (subsectionItem: any, subsectionIndex: number) => {
          subsectionItem.questions.forEach((question: Question) => {
            const response = userResponse[question.id];

            const isEmpty =
              response === undefined ||
              response === null ||
              (typeof response === 'string' && response.trim() === '') ||
              (Array.isArray(response) && response.length === 0);

            if (isEmpty) {
              unansweredQuestions.push({
                question,
                sectionIndex,
                subsectionIndex,
              });
            }
          });
        }
      );
    });

    if (unansweredQuestions.length > 0) {
      const firstUnanswered = unansweredQuestions[0];

      setCurrentSection(firstUnanswered.sectionIndex);
      setSubsection(firstUnanswered.subsectionIndex);

      setTimeout(() => {
        document.getElementById('question-start')?.scrollIntoView({
          behavior: 'smooth',
        });
      }, 200);

      Toast.showErrorMessage(
        `${unansweredQuestions.length} question${
          unansweredQuestions.length > 1 ? 's are' : ' is'
        } unanswered. Please complete all questions before submitting.`
      );

      return false;
    }

    return true;
  };

  const actionOnSubmit = async (): Promise<void> => {
    const isValid = validateAllResponses();
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = await preparePayload('Completed');
      submitAssessment(payload, (status: boolean) => {
        if (status) {
          actionBackToAssessments(true);
        }
        setIsSubmitting(false);
      });
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const actionOnSaveProgress = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const payload = await preparePayload('In Progress');
      submitAssessment(payload, () => {
        Toast.showSuccessMessage('Your assessment progress has been saved.');
        setIsSubmitting(false);
        if (quickEdit) {
          actionBackToAssessments(true);
        }
      });
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  // Child Events
  const actionSaveUserResponse = (value: any, question: Question): void => {
    setUserResponse((prev) => ({ ...prev, [question.id]: value }));
  };

  // Hooks

  // UI Elements
  return (
    <>
      {isLoadingDetails && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoadingDetails && (
        <Box sx={{ px: 6, py: 4 }}>
          {!isEditMode && !quickEdit && (
            <>
              <Button
                className="btn-small mb-3"
                variant="outlined"
                startIcon={<ChevronLeftIcon />}
                onClick={() => actionBackToAssessments(false)}
              >
                Back to Assessments
              </Button>

              {!isBulkAssessment && (
                <>
                  <Typography variant="h2">Continue Risk Assessment</Typography>
                  {datasets.length > 0 && (
                    <Typography variant="h6">
                      Continue your assessment for Dataset: {datasets[0]._id}
                    </Typography>
                  )}
                  {aiSystems.length > 0 && (
                    <Typography variant="h6">
                      Continue your assessment for AI System: {aiSystems[0]._id}
                    </Typography>
                  )}
                </>
              )}
              {isBulkAssessment && (
                <>
                  <Typography variant="h2">
                    Complete Bulk Risk Assessment
                  </Typography>
                  <Typography variant="h6">
                    Complete this assessment once to apply it to all selected
                    entities
                  </Typography>
                  <Box
                    className="my-4"
                    style={{
                      border: '1px solid lightgray',
                      borderRadius: '10px',
                      padding: 10,
                      marginTop: 16,
                    }}
                  >
                    <Typography variant="h2" className="mb-4">
                      Assessment Coverage{' '}
                      <Chip
                        label={`${datasets.length + aiSystems.length} entities`}
                        size="small"
                      />
                    </Typography>
                    <Typography variant="h6" className="fw-6">
                      {templateDetail.templateName}
                    </Typography>
                    <Typography variant="body1" className="mb-4">
                      {templateDetail.description}
                    </Typography>
                    {datasets.length > 0 && (
                      <>
                        <Typography variant="h6" className="fw-6">
                          Datasets({datasets.length})
                        </Typography>
                        {datasets.map((d: any) => (
                          <Chip
                            label={d.title}
                            key={d.title}
                            size="small"
                            className="mr-2"
                          />
                        ))}
                      </>
                    )}
                    {aiSystems.length > 0 && (
                      <>
                        <Typography variant="h6" className="fw-6 mt-4">
                          AI Systems(
                          {aiSystems.length})
                        </Typography>
                        {aiSystems.map((item: any) => (
                          <Chip
                            label={item.title}
                            key={item.title}
                            size="small"
                            className="mr-2"
                          />
                        ))}
                      </>
                    )}
                  </Box>
                </>
              )}
            </>
          )}

          <Card variant="outlined" id="question-start" className="mt-4">
            <CardHeader
              title={templateDetail.templateName}
              subheader={
                <Typography className="">
                  Section {currentSection + 1}/{templateDetail.sections.length}{' '}
                  {templateDetail.sections[currentSection].title}
                </Typography>
              }
            />
            <Divider />
            <CardContent>
              <Typography variant="h3" className="fw-4">
                {
                  templateDetail.sections[currentSection].subsections[
                    subsection
                  ].title
                }{' '}
                ({subsection + 1}/{subsections.length})
              </Typography>
              {questions.map((question: Question, index: number) => (
                <>
                  {question.type === 'SelectOne' && (
                    <SelectOne
                      key={`s${question.id}`}
                      question={question}
                      index={index}
                      onChange={(value) => {
                        actionSaveUserResponse(value, question);
                      }}
                      defaultValue={userResponse[question.id] || ''}
                    />
                  )}
                  {question.type === 'SelectMany' && (
                    <SelectMany
                      key={`s${question.id}`}
                      question={question}
                      index={index}
                      onChange={(value) => {
                        actionSaveUserResponse(value, question);
                      }}
                      defaultValue={userResponse[question.id] || ''}
                    />
                  )}
                  {['LongText', 'ShortText'].includes(question.type) && (
                    <ATextField
                      key={`t${question.id}`}
                      question={question}
                      index={index}
                      onChange={(value) => {
                        actionSaveUserResponse(value, question);
                      }}
                      defaultValue={userResponse[question.id] || ''}
                    />
                  )}
                </>
              ))}
            </CardContent>

            <Divider />
            <Stack alignItems="center" justifyContent="center" direction="row">
              <Button
                disabled={currentSection === 0 && subsection === 0}
                onClick={actionOnClickBack}
                size="small"
                startIcon={<ChevronLeftIcon />}
              >
                Previous
              </Button>
              <Typography>
                Section {currentSection + 1} of {totalSections}
              </Typography>
              <Button
                disabled={
                  currentSection === totalSections - 1 &&
                  subsection === subsections.length - 1
                }
                onClick={actionOnClickNext}
                size="small"
                endIcon={<NavigateNextIcon />}
              >
                Next
              </Button>
            </Stack>
            <Stack
              className="p-4"
              alignItems="center"
              justifyContent="space-between"
              direction="row"
            >
              {guestMode ? (
                <Box />
              ) : (
                <AppPrimaryButtonOutlined
                  className="btn-small"
                  title={
                    isBulkAssessment ? 'Save Bulk Progress' : 'Save Progress'
                  }
                  onClick={actionOnSaveProgress}
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                  loadingText="Saving"
                />
              )}

              <Box>
                {!guestMode && (
                  <AppPrimaryButtonOutlined
                    title="Exit"
                    className="mr-2 btn-small"
                    onClick={() => actionBackToAssessments(false)}
                  />
                )}
                <AppPrimaryButton
                  loadingText="Submitting"
                  onClick={actionOnSubmit}
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                  className="btn-small"
                  title={
                    isBulkAssessment
                      ? 'Complete Bulk Assessment'
                      : 'Complete Assessment'
                  }
                />
              </Box>
            </Stack>
          </Card>
        </Box>
      )}
    </>
  );
};

export default RiskAssessmentDetail;
